import { tsv } from "@faire/slate/lib/tsv";
import type { ReactNode } from "react";

import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";

import {
  type BrandCartAction,
  BrandGroupHeader,
  resolveBrandGroupSummary,
} from "./BrandGroupHeader";
import type { BrandCart } from "./useBrandCart";

/**
 * How brand groups are separated:
 * - "dividers": a rule between groups (Figma 9687:321032).
 * - "cards": each group in a 1px muted border with 16px padding and 4px radius.
 */
export type BrandGroupFraming = "dividers" | "cards";

interface BrandGroupedProductsProps {
  products: FaireProductEntity[];
  cart: BrandCart;
  cartAction: BrandCartAction;
  framing?: BrandGroupFraming;
  renderTile: (product: FaireProductEntity) => ReactNode;
}

/**
 * Brands view: products grouped under a brand lockup per brand. The grids
 * drop a column while the side cart narrows the page.
 */
export const BrandGroupedProducts = ({
  products,
  cart,
  cartAction,
  framing = "dividers",
  renderTile,
}: BrandGroupedProductsProps) => (
  <div className={brandGroupListStyle.cls({ framing })}>
    {groupProductsByBrand(products).map((group, index) => (
      <div
        key={group.brandName}
        className={brandGroupStyle.cls({ framing, first: index === 0 })}
      >
        <BrandGroupHeader
          brand={resolveBrandGroupSummary(group.brandName, group.products)}
          cartSubtotal={cart.cartSubtotalForBrand(group.brandName)}
          cartItemCount={cart.cartItemCountForBrand(group.brandName)}
          isCartOpen={
            cart.isSideCartOpen && cart.sideCartBrandName === group.brandName
          }
          cartAction={cartAction}
          onToggleCart={() => cart.toggleSideCart(group.brandName)}
        />
        <div
          className={savedProductGridStyle.cls({ compact: cart.isSideCartOpen })}
        >
          {group.products.map(renderTile)}
        </div>
      </div>
    ))}
  </div>
);

export const groupProductsByBrand = (
  products: FaireProductEntity[]
): { brandName: string; products: FaireProductEntity[] }[] => {
  const groups = new Map<string, FaireProductEntity[]>();
  for (const product of products) {
    const brandProducts = groups.get(product.brandName) ?? [];
    groups.set(product.brandName, [...brandProducts, product]);
  }
  return [...groups.entries()].map(([brandName, brandProducts]) => ({
    brandName,
    products: brandProducts,
  }));
};

/** Saved product grid; one fewer desktop column while the side cart is open. */
export const savedProductGridStyle = tsv({
  base: ["grid grid-cols-2 gap-4 md:grid-cols-4"],
  variants: {
    compact: {
      true: "lg:grid-cols-5",
      false: "lg:grid-cols-6",
    },
  },
});

const brandGroupListStyle = tsv({
  base: ["flex flex-col"],
  variants: {
    framing: {
      dividers: "",
      cards: "gap-4",
    },
  },
});

const brandGroupStyle = tsv({
  base: ["flex flex-col gap-4"],
  variants: {
    framing: {
      dividers: "",
      cards: "border-fs-border-muted rounded border p-4",
    },
    first: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      framing: "dividers",
      first: false,
      className: "border-fs-border-muted mt-6 border-t pt-6",
    },
  ],
});
